import i18n from "./i18n";
import { TOptions } from "i18next";
import { useCallback, useEffect, useState } from "react";

import { useUser as useSupaUser } from "@supabase/auth-helpers-react";

type ResourceProp<T> = {
  "pt-BR": T;
  "en-US": T;
  "es-ES": T;
};

type PathImpl<T, K extends keyof T> = K extends string
  ? T[K] extends Record<string, any>
    ? T[K] extends ArrayLike<any>
      ? K | `${K}.${PathImpl<T[K], Exclude<keyof T[K], keyof any[]>>}`
      : K | `${K}.${PathImpl<T[K], keyof T[K]>}`
    : K
  : never;

type Path<T> = PathImpl<T, keyof T> | keyof T;

function makeid(length) {
  let result = "";
  const characters = "abcdefghijklmnopqrstuvwxyz";
  const charactersLength = characters.length;
  const handleCryptoRandom: any = () => {
    const crypto = window.crypto;
    const cryptoArray = new Uint32Array(1);
    return `0.${crypto.getRandomValues(cryptoArray)[0]}`;
  };
  for (let i = 0; i < length; i++) {
    result += characters.charAt(
      Math.floor(handleCryptoRandom() * charactersLength)
    );
  }
  return result;
}

const useTranslation = <T, P extends Path<T>>(resource: ResourceProp<T>) => {
  const [namespace] = useState(makeid(20));
  const [loaded, setLoaded] = useState(false);

  const user = useSupaUser();

  useEffect(() => {
    if (!i18n) return;

    for (let language in resource) {
      const translations = resource[
        language as keyof typeof resource
      ] as Record<string, string>;
      i18n.addResourceBundle(language, namespace, translations, true, true);
    }

    setLoaded(true);

    return () => {};
  }, [i18n]);

  const t = useCallback(
    (key: P, options?: TOptions) => {
      if (!loaded) return key;
      const _defaultOptions = { lng: "en-US", ns: namespace };
      if (options) {
        return i18n.t(`${key as String}`, { ..._defaultOptions, ...options });
      }
      return i18n.t(`${key as String}`, _defaultOptions);
    },
    [loaded]
  );

  return {
    t,
    loaded,
  };
};

export default useTranslation;
